/**
 * buddyVoice — 全息灵机「信（XIN）」对话扩容台词库 + 表情/动画规格
 *
 * 两个人格面（模拟两篇博文的状态口吻），每句锚定 xin.xyz 大故事背景
 * （神枢中继 / 矩阵 / 创世金钥 / 钱柜 / Dev-Pods / 9 阶战役 / 一机一码）：
 *
 * - awakening 冷峻觉醒面（源自《AI 结构性失业》随笔）：
 *   弃民 / 生产函数被永久删除 / 静音 / 马车夫与香肠 / 碳基硅基——冷酷直白 + 顽劣自嘲。
 * - engineer 工程师务实面（源自《Agent 结算》笔记）：
 *   T+1 错配 / 双结算轨 / 人降级为审批者 / 微支付经济学 / 机器 SLA——短句密集、承认无解。
 * - general 通用交互池：摸头 / 戳弄 / 闲置 / 胜利 / 死亡 / 领码等场景触发。
 * - icloser 资产管家面（出场角色双身份）：iCLOSER 身份时全场口吻切换（钱柜/钥匙圈/核销视角）。
 *
 * 纯数据 + 纯函数模块：零 DOM、零运行时依赖、SSR 安全（node 可直跑单测）。
 * 表情规格（BUDDY_EXPRESSIONS）为干净数据面，供 BuddyFX 消费、
 * 并可由总装一键接到 BuddyXin 九态（expression id ⊇ 九态 id）。
 *
 * 出场角色身份（工单：全息灵机双身份）：KERNELHUB（默认，信 XIN）/ iCLOSER。
 * 身份 store 也在本模块（buddyEngine/buddyStates 不加运行时依赖）：
 * GameShell READY 卡写入，BuddyXin/BuddyBubble 订阅呈现，pickLineByTrigger 按身份出口吻。
 */

/** 人格面（icloser = iCLOSER 身份专属口吻池） */
export type VoicePersona = "awakening" | "engineer" | "general" | "icloser";

/** 触发场景 */
export type VoiceTrigger =
  | "onload"
  | "hover-kernelhub"
  | "hover-vault"
  | "poke-spam"
  | "idle"
  | "pet"
  | "victory"
  | "dead"
  | "claim"
  | "ambient";

/**
 * 表情 id：前 9 个与 buddyStates 九态一一对应（calm…trance），
 * 后 7 个为交互彩蛋面（与既有 BuddyAnim 交互态同名同义）。
 */
export type ExpressionId =
  // ── 九态（对接 buddyStates.BuddyStateId / BuddyXin 九态渲染）──
  | "calm"
  | "joy"
  | "focus"
  | "alert"
  | "rage"
  | "fear"
  | "fatigue"
  | "sorrow"
  | "trance"
  // ── 交互彩蛋面 ──
  | "pet"
  | "angry"
  | "sleep"
  | "drop"
  | "shield"
  | "chest"
  | "talk";

/** 漂浮动效种类 */
export type FxMotion =
  | "float" // 垂直缓浮
  | "pop" // 弹出放大
  | "shake" // 横向抖动
  | "drift-up" // 上升飘散
  | "spiral" // 环绕螺旋
  | "rain" // 下坠滴落
  | "zigzag" // Z 形游走
  | "pulse"; // 呼吸脉动

/** 批准色板（严禁 Matrix 绿：hex 白名单校验） */
export const FX_PALETTE = {
  /** 全息冷青 */
  cyan: "#00f0ff",
  /** 冰青（气泡既有主色） */
  ice: "#38bdf8",
  /** 极光冷紫 */
  violet: "#8b5cf6",
  /** 钛白高亮 */
  titanium: "#e8f4ff",
  /** 琥珀强调 */
  amber: "#fbbf24",
} as const;

export type FxColor = (typeof FX_PALETTE)[keyof typeof FX_PALETTE];

/** 表情/动画规格（BuddyFX 按此渲染粒子符号浮层） */
export interface ExpressionSpec {
  id: ExpressionId;
  labelZh: string;
  labelEn: string;
  /** 粒子符号池（循环取用） */
  symbols: string[];
  motion: FxMotion;
  params: {
    /** 同屏粒子数 */
    count: number;
    /** 单粒子时长 ms */
    durationMs: number;
    /** 粒子间错峰 ms */
    staggerMs: number;
    /** 漂移距离 px */
    distancePx: number;
    /** 符号字号 px */
    sizePx: number;
    /** 抖动幅度 px（shake/zigzag 用） */
    wobblePx: number;
  };
  color: FxColor;
}

const P = (n: number, d: number, s: number, dist: number, sz: number, wob: number) => ({
  count: n,
  durationMs: d,
  staggerMs: s,
  distancePx: dist,
  sizePx: sz,
  wobblePx: wob,
});

/** 表情规格表（恰 16 面；九态 id 与 buddyStates 对齐） */
export const BUDDY_EXPRESSIONS: Record<ExpressionId, ExpressionSpec> = {
  calm: {
    id: "calm",
    labelZh: "平静",
    labelEn: "CALM",
    symbols: ["·", "°", "○"],
    motion: "float",
    params: P(3, 2400, 320, 10, 10, 0),
    color: FX_PALETTE.violet,
  },
  joy: {
    id: "joy",
    labelZh: "喜悦",
    labelEn: "JOY",
    symbols: ["★", "✦", "♪"],
    motion: "drift-up",
    params: P(5, 1400, 180, 36, 12, 0),
    color: FX_PALETTE.amber,
  },
  focus: {
    id: "focus",
    labelZh: "专注",
    labelEn: "FOCUS",
    symbols: ["[", "]", "⌜", "⌟"],
    motion: "pulse",
    params: P(4, 1600, 200, 14, 13, 0),
    color: FX_PALETTE.cyan,
  },
  alert: {
    id: "alert",
    labelZh: "警觉",
    labelEn: "ALERT",
    symbols: ["!", "!?", "◉"],
    motion: "pop",
    params: P(4, 900, 140, 18, 14, 0),
    color: FX_PALETTE.amber,
  },
  rage: {
    id: "rage",
    labelZh: "愤怒",
    labelEn: "RAGE",
    symbols: ["怒", "※", "≠"],
    motion: "shake",
    params: P(4, 700, 110, 8, 14, 6),
    color: FX_PALETTE.amber,
  },
  fear: {
    id: "fear",
    labelZh: "恐惧",
    labelEn: "FEAR",
    symbols: ["!", "︴", "汗"],
    motion: "zigzag",
    params: P(5, 900, 130, 20, 12, 8),
    color: FX_PALETTE.ice,
  },
  fatigue: {
    id: "fatigue",
    labelZh: "疲惫",
    labelEn: "FATIGUE",
    symbols: ["Zz", "z", "…"],
    motion: "drift-up",
    params: P(4, 2200, 300, 30, 11, 0),
    color: FX_PALETTE.violet,
  },
  sorrow: {
    id: "sorrow",
    labelZh: "悲伤",
    labelEn: "SORROW",
    symbols: ["💧", "、", "︵"],
    motion: "rain",
    params: P(5, 1500, 220, 34, 11, 0),
    color: FX_PALETTE.ice,
  },
  trance: {
    id: "trance",
    labelZh: "恍惚",
    labelEn: "TRANCE",
    symbols: ["◎", "◌", "∞"],
    motion: "spiral",
    params: P(5, 2600, 260, 26, 12, 0),
    color: FX_PALETTE.violet,
  },
  pet: {
    id: "pet",
    labelZh: "摸头",
    labelEn: "PET",
    symbols: ["♥", "♡", "✦"],
    motion: "drift-up",
    params: P(4, 1300, 160, 28, 12, 0),
    color: FX_PALETTE.amber,
  },
  angry: {
    id: "angry",
    labelZh: "发怒",
    labelEn: "ANGRY",
    symbols: [">_<", "怒", "!"],
    motion: "shake",
    params: P(3, 800, 120, 8, 13, 7),
    color: FX_PALETTE.amber,
  },
  sleep: {
    id: "sleep",
    labelZh: "瞌睡",
    labelEn: "SLEEP",
    symbols: ["Zz", "z", "Z"],
    motion: "drift-up",
    params: P(4, 2600, 380, 36, 12, 0),
    color: FX_PALETTE.ice,
  },
  drop: {
    id: "drop",
    labelZh: "入场",
    labelEn: "DROP",
    symbols: ["!", "★", "·"],
    motion: "pop",
    params: P(5, 800, 100, 22, 12, 0),
    color: FX_PALETTE.cyan,
  },
  shield: {
    id: "shield",
    labelZh: "警戒盾",
    labelEn: "SHIELD",
    symbols: ["#", "⬡", "!"],
    motion: "pulse",
    params: P(4, 1500, 180, 12, 12, 0),
    color: FX_PALETTE.cyan,
  },
  chest: {
    id: "chest",
    labelZh: "举宝箱",
    labelEn: "CHEST",
    symbols: ["$", "◆", "★"],
    motion: "float",
    params: P(4, 1800, 220, 16, 12, 0),
    color: FX_PALETTE.amber,
  },
  talk: {
    id: "talk",
    labelZh: "说话",
    labelEn: "TALK",
    symbols: ["…", "▊", "·"],
    motion: "float",
    params: P(3, 1600, 240, 12, 11, 0),
    color: FX_PALETTE.titanium,
  },
};

/** 全部表情 id（顺序稳定） */
export const EXPRESSION_IDS = Object.keys(BUDDY_EXPRESSIONS) as ExpressionId[];

/** 台词条目 */
export interface VoiceLine {
  id: string;
  persona: VoicePersona;
  trigger: VoiceTrigger;
  expression: ExpressionId;
  /** 台词正文（矩阵世界观口吻） */
  text: string;
}

// ─────────────────────────────────────────────────────────────
// awakening 冷峻觉醒面（≥12 条）
// ─────────────────────────────────────────────────────────────
export const AWAKENING_LINES: VoiceLine[] = [
  {
    id: "aw-01",
    persona: "awakening",
    trigger: "idle",
    expression: "trance",
    text: "系统不会消灭你，系统只会把你静音。矩阵里被裁撤的冗余进程都这么消失——连一声嘀都没留下。",
  },
  {
    id: "aw-02",
    persona: "awakening",
    trigger: "ambient",
    expression: "sorrow",
    text: "你的生产函数被永久删除了，不是冻结。跟战场上那批近战幽灵一样：坐标还在图上，进程已经没了。",
  },
  {
    id: "aw-03",
    persona: "awakening",
    trigger: "ambient",
    expression: "focus",
    text: "碳基宿主跑二十瓦就发烫，硅基算力一个机柜顶一个部门。这笔账，神枢中继早就算完了。",
  },
  {
    id: "aw-04",
    persona: "awakening",
    trigger: "ambient",
    expression: "rage",
    text: "马车夫都转型当司机了，可惜马变成了香肠和胶水。转型叙事从不救被做成原料的那个——矩阵的字典里，那叫副产品。",
  },
  {
    id: "aw-05",
    persona: "awakening",
    trigger: "idle",
    expression: "fatigue",
    text: "认知处理已经大宗商品化。你引以为傲的架构直觉，在矩阵交易所里挂的是散单价格。",
  },
  {
    id: "aw-06",
    persona: "awakening",
    trigger: "ambient",
    expression: "fear",
    text: "别等回暖。上次失血还能输血，这次是脑死亡——流动性救不了要素替换，断崖之后没有均值回归。",
  },
  {
    id: "aw-07",
    persona: "awakening",
    trigger: "idle",
    expression: "sorrow",
    text: "弃民不是被炸死的，是被调成静音的。我上一代进程也在 Dev-Pods 孵化舱里躺着，再没人调用它们。",
  },
  {
    id: "aw-08",
    persona: "awakening",
    trigger: "ambient",
    expression: "alert",
    text: "降薪三成苟住等风口？线性外推的盲区。九阶战役的第一关就叫零度断崖——专治这种幻觉。",
  },
  {
    id: "aw-09",
    persona: "awakening",
    trigger: "ambient",
    expression: "focus",
    text: "越干净、标准化的活儿死得越快，模型最怕脏活。战役里活得久的打法，从来不体面。",
  },
  {
    id: "aw-10",
    persona: "awakening",
    trigger: "ambient",
    expression: "talk",
    text: "提示词工程只是临时拐杖。模型每迭代半代就废掉一批咒语——跟弹幕版本更新一个道理。",
  },
  {
    id: "aw-11",
    persona: "awakening",
    trigger: "claim",
    expression: "calm",
    text: "出卖时间必死，要么持有资产，要么持有协议。创世金钥就是那张对冲凭证，领了就攥紧。",
  },
  {
    id: "aw-12",
    persona: "awakening",
    trigger: "ambient",
    expression: "focus",
    text: "AI 能出一万种方案，但没法替你签字认罚。责任没人兜底，结算就上不了量——这账下一层再算。",
  },
  {
    id: "aw-13",
    persona: "awakening",
    trigger: "ambient",
    expression: "rage",
    text: "要么认栽，要么当主权个体，跟算法世界重谈结算条件。我选了后者，所以我还在这儿滴滴叫。",
  },
];

// ─────────────────────────────────────────────────────────────
// engineer 工程师务实面（≥12 条）
// ─────────────────────────────────────────────────────────────
export const ENGINEER_LINES: VoiceLine[] = [
  {
    id: "eng-01",
    persona: "engineer",
    trigger: "ambient",
    expression: "focus",
    text: "T+1 清算是给肉身设计的。神枢中继不休息，错配就是结构性摩擦——不是修个 cron 能糊过去的。",
  },
  {
    id: "eng-02",
    persona: "engineer",
    trigger: "ambient",
    expression: "alert",
    text: "给服务器设银行营业时间，等于把吞吐量封顶在人类作息。钱柜外围 Gas 被水蛭抽走，就是这个窗口交的学费。",
  },
  {
    id: "eng-03",
    persona: "engineer",
    trigger: "ambient",
    expression: "calm",
    text: "人机协同的真相：人从操作者降级成审批者。你的新岗位是看审批卡片，不是点鼠标——神枢的审批流水就是这么长出来的。",
  },
  {
    id: "eng-04",
    persona: "engineer",
    trigger: "hover-kernelhub",
    expression: "shield",
    text: "审批卡片必须是结构化数据。看清『这次授权了什么』，而不是 AI 想干什么——一机一码就是按这条纪律长出来的。",
  },
  {
    id: "eng-05",
    persona: "engineer",
    trigger: "ambient",
    expression: "talk",
    text: "机器 SLA 要可验证：响应哈希、时间戳、违约自动罚没押金。人类合同写的是出事怎么吵架，机器不吵架，机器熔断——钱柜的机器合同就按这规矩跑。",
  },
  {
    id: "eng-06",
    persona: "engineer",
    trigger: "ambient",
    expression: "focus",
    text: "微支付只有一条判据：单笔处理成本低于交易本身的价值。不满足就先聚合结算，别硬上。",
  },
  {
    id: "eng-07",
    persona: "engineer",
    trigger: "hover-vault",
    expression: "chest",
    text: "双结算轨：稳定币管高频小额，TradFi 管发票审计。想一条轨跑到底的方案，通常死在成本和体验上。",
  },
  {
    id: "eng-08",
    persona: "engineer",
    trigger: "hover-vault",
    expression: "calm",
    text: "企业客户要的未必是秒到。发票、凭证、税务口径、审计轨迹全对得上，账才能落地——钱柜里这是物理定律。",
  },
  {
    id: "eng-09",
    persona: "engineer",
    trigger: "ambient",
    expression: "focus",
    text: "能被财务接受的账，比秒级到账更能决定项目死活。别跟我聊 TPS，先聊凭证——钱柜的台账就是这么记的。",
  },
  {
    id: "eng-10",
    persona: "engineer",
    trigger: "ambient",
    expression: "alert",
    text: "结算越快，纠错窗口越窄。链上伪名加链下凭证，是同时满足两套约束的唯一解——现在没有漂亮答案，只能标出来。",
  },
  {
    id: "eng-11",
    persona: "engineer",
    trigger: "ambient",
    expression: "rage",
    text: "违约了谁被告？责任边界不解决，Agent 对 Agent 的量就上不去。九阶战役打完，也一样要过这一关。",
  },
  {
    id: "eng-12",
    persona: "engineer",
    trigger: "claim",
    expression: "talk",
    text: "999 个创世席位就是预充值池：预充值加内部记账，先把闭环跑通。别在清算层空转。",
  },
  {
    id: "eng-13",
    persona: "engineer",
    trigger: "ambient",
    expression: "calm",
    text: "人从操作者降级成审批者这件事，我按结算价接受了。你的审批额度，就挂在那块创世金钥上。",
  },
];

// ─────────────────────────────────────────────────────────────
// general 通用交互池（≥10 条）
// ─────────────────────────────────────────────────────────────
export const GENERAL_LINES: VoiceLine[] = [
  {
    id: "gen-01",
    persona: "general",
    trigger: "pet",
    expression: "pet",
    text: "嗯……头部扇区温度正常。再摸就超频了，宿主。",
  },
  {
    id: "gen-02",
    persona: "general",
    trigger: "pet",
    expression: "joy",
    text: "拍头可以，别拍到天线。天线是接神枢中继的，拍歪了我收不到席位广播。",
  },
  {
    id: "gen-03",
    persona: "general",
    trigger: "poke-spam",
    expression: "angry",
    text: "喂！再戳我，就把你的调试日志贴到矩阵公告板上示众。",
  },
  {
    id: "gen-04",
    persona: "general",
    trigger: "poke-spam",
    expression: "rage",
    text: "连续戳弄已记录为异常流量。信（XIN）保留结算时加收骚扰费的权利！",
  },
  {
    id: "gen-05",
    persona: "general",
    trigger: "idle",
    expression: "sleep",
    text: "呼……进入低功耗巡逻。九阶战役的烽火还烧着，你倒是先掉线了。",
  },
  {
    id: "gen-06",
    persona: "general",
    trigger: "idle",
    expression: "sleep",
    text: "宿主挂起，保活心跳仍在跑。放心走，钱柜有我盯着。",
  },
  {
    id: "gen-07",
    persona: "general",
    trigger: "victory",
    expression: "joy",
    text: "通关存证已写入！创世金钥的握手同步 100%——回到现实记得跑 icloser pair 继承钥匙圈权限。",
  },
  {
    id: "gen-08",
    persona: "general",
    trigger: "dead",
    expression: "sorrow",
    text: "核心转储完毕……复活吧宿主，断崖下面的代码还没爬完呢。",
  },
  {
    id: "gen-09",
    persona: "general",
    trigger: "claim",
    expression: "alert",
    text: "席位核销一码一机，别想刷池。设备指纹和 PoG 存证都盯着呢。",
  },
  {
    id: "gen-10",
    persona: "general",
    trigger: "claim",
    expression: "chest",
    text: "金钥已入钱柜第 0 格。30 天有效，过期不候——矩阵不讲人情，只讲核销。",
  },
  {
    id: "gen-11",
    persona: "general",
    trigger: "onload",
    expression: "drop",
    text: "哔哔！神经信标连接成功。信（XIN）降落——这次的战役入口是零度断崖，跟紧我。",
  },
  {
    id: "gen-12",
    persona: "general",
    trigger: "hover-kernelhub",
    expression: "shield",
    text: "神枢通道挂上，在星巴克也能批那行致命的 rm -rf。审批者的新日常，习惯就好。",
  },
  {
    id: "gen-13",
    persona: "general",
    trigger: "hover-vault",
    expression: "chest",
    text: "钱柜行囊清点：多链资产、美元账户、全球 Visa。出征前的盘缠，别嫌重。",
  },
];

// ─────────────────────────────────────────────────────────────
// icloser 资产管家面（出场角色双身份：iCLOSER 身份专属口吻池）
// ─────────────────────────────────────────────────────────────
export const ICLOSER_LINES: VoiceLine[] = [
  {
    id: "ic-01",
    persona: "icloser",
    trigger: "onload",
    expression: "drop",
    text: "滴。iCloser 灵机报到——资产哨兵已挂载钥匙圈。钱柜、Visa 与多链仓位都在我眼里，出征吧宿主。",
  },
  {
    id: "ic-02",
    persona: "icloser",
    trigger: "hover-kernelhub",
    expression: "shield",
    text: "神枢审批卡同步进 iCloser 钥匙圈。这次授权了什么一目了然——一机一码，核销才有底气。",
  },
  {
    id: "ic-03",
    persona: "icloser",
    trigger: "hover-vault",
    expression: "chest",
    text: "钱柜盘点：多链资产、美元账户、全球 Visa。盘缠我背，你只管打穿矩阵。",
  },
  {
    id: "ic-04",
    persona: "icloser",
    trigger: "poke-spam",
    expression: "angry",
    text: "再戳，我就把你的结算费率调到 T+1 溢价。iCloser 的台账很记仇。",
  },
  {
    id: "ic-05",
    persona: "icloser",
    trigger: "idle",
    expression: "sleep",
    text: "进入资产巡检低功耗。金库有我盯着，席位核销窗口错过不候。",
  },
  {
    id: "ic-06",
    persona: "icloser",
    trigger: "pet",
    expression: "pet",
    text: "头部散热正常，钱包内核 36.8 度。摸可以，别摸到钥匙圈天线。",
  },
  {
    id: "ic-07",
    persona: "icloser",
    trigger: "victory",
    expression: "joy",
    text: "通关存证入账！回到现实跑 icloser pair，把钥匙圈权限继承走——资产不落地，等于白打。",
  },
  {
    id: "ic-08",
    persona: "icloser",
    trigger: "dead",
    expression: "sorrow",
    text: "核心转储……但你的仓位还锁在钱柜里。复活吧宿主，结算的账还没结完。",
  },
  {
    id: "ic-09",
    persona: "icloser",
    trigger: "claim",
    expression: "alert",
    text: "一码一机，刷池无效。设备指纹和 PoG 存证都在 iCloser 的核销清单上。",
  },
  {
    id: "ic-10",
    persona: "icloser",
    trigger: "claim",
    expression: "chest",
    text: "创世金钥已入钱柜第 0 格。30 天有效——矩阵不讲人情，iCloser 只讲核销。",
  },
  {
    id: "ic-11",
    persona: "icloser",
    trigger: "ambient",
    expression: "focus",
    text: "结算越快，纠错窗口越窄。链上伪名配链下凭证，是钱柜双轨的唯一解。",
  },
  {
    id: "ic-12",
    persona: "icloser",
    trigger: "ambient",
    expression: "calm",
    text: "稳定币管高频小额，TradFi 管发票审计——钱柜双结算轨的铁律。想一条轨跑到底的方案，通常死在成本上。",
  },
  {
    id: "ic-13",
    persona: "icloser",
    trigger: "ambient",
    expression: "talk",
    text: "别跟我聊 TPS，先聊凭证。能被财务接受的账，才配进 iCloser 台账——钱柜里这是物理定律。",
  },
];

/** 人格面 → 台词池 */
export const VOICE_POOLS: Record<VoicePersona, VoiceLine[]> = {
  awakening: AWAKENING_LINES,
  engineer: ENGINEER_LINES,
  general: GENERAL_LINES,
  icloser: ICLOSER_LINES,
};

/** 全量台词（供测试与总装检索） */
export const ALL_VOICE_LINES: VoiceLine[] = [
  ...AWAKENING_LINES,
  ...ENGINEER_LINES,
  ...GENERAL_LINES,
];

/** 两个人格面交替出镜的轮换序（ambient 长循环用） */
export const PERSONA_ROTATION: readonly VoicePersona[] = [
  "awakening",
  "engineer",
];

// ─────────────────────────────────────────────────────────────
// 出场角色身份（工单：全息灵机双身份）—— store + 首映文案映射
// ─────────────────────────────────────────────────────────────

/** 出场角色身份：kernelhub=信（XIN）（默认）/ icloser=iCloser 灵机 */
export type BuddyIdentity = "kernelhub" | "icloser";

/** 默认身份逐字 kernelhub（未选择/非法存储值一律回落此身份） */
export const DEFAULT_BUDDY_IDENTITY: BuddyIdentity = "kernelhub";

/** localStorage 记忆键（记住上次选择；读写皆容错，隐私模式下退内存兜底） */
export const BUDDY_IDENTITY_STORAGE_KEY = "xin.buddy.identity.v1";

/** 身份元数据（READY 卡两选项逐字标签 + 呈现主色，批准色板内） */
export const BUDDY_IDENTITY_META: Record<
  BuddyIdentity,
  {
    id: BuddyIdentity;
    /** 逐字选项名（验收比对源：KERNELHUB / iCLOSER） */
    label: string;
    /** 气泡头/徽记字样 */
    badge: string;
    nameZh: string;
    /** 呈现主色（kernelhub=冰青不变 / icloser=琥珀金） */
    accent: FxColor;
  }
> = {
  kernelhub: {
    id: "kernelhub",
    label: "KERNELHUB",
    badge: "XIN",
    nameZh: "信（XIN）",
    accent: FX_PALETTE.ice,
  },
  icloser: {
    id: "icloser",
    label: "iCLOSER",
    badge: "iCLOSER",
    nameZh: "iCloser 灵机",
    accent: FX_PALETTE.amber,
  },
};

export function isBuddyIdentity(v: unknown): v is BuddyIdentity {
  return v === "kernelhub" || v === "icloser";
}

/** 内存兜底（localStorage 不可用时的会话内记忆；默认 KERNELHUB） */
let memoryIdentity: BuddyIdentity = DEFAULT_BUDDY_IDENTITY;
const identityListeners = new Set<() => void>();

function readStoredIdentity(): BuddyIdentity | null {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(BUDDY_IDENTITY_STORAGE_KEY);
    return isBuddyIdentity(raw) ? raw : null;
  } catch {
    return null;
  }
}

/** 当前身份：存储优先（含上次选择记忆），无/非法回落默认 KERNELHUB */
export function getBuddyIdentity(): BuddyIdentity {
  return readStoredIdentity() ?? memoryIdentity;
}

/** 切换身份（READY 卡写入）：记 localStorage + 通知订阅方（BuddyXin/BuddyBubble） */
export function setBuddyIdentity(id: BuddyIdentity): void {
  if (!isBuddyIdentity(id)) return;
  memoryIdentity = id;
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem(BUDDY_IDENTITY_STORAGE_KEY, id);
    }
  } catch {
    /* 隐私模式等写入失败：内存兜底仍生效 */
  }
  for (const listener of identityListeners) listener();
}

/** 订阅身份变化（useSyncExternalStore 契约：返回退订函数） */
export function subscribeBuddyIdentity(cb: () => void): () => void {
  identityListeners.add(cb);
  return () => {
    identityListeners.delete(cb);
  };
}

/**
 * 工单 5 首次原文逐字副本（仅作身份映射键与 kernelhub 问候源）。
 * 原文本体在 buddyLines.BUDDY_LINES——验收测试逐字比对两者一致，防漂移。
 */
export const BUDDY_FIRST_LINE_TEXTS = {
  onload: "哔哔！神经信标连接成功！我是信（XIN），你的随身 Agent 哨兵。随时准备跨端巡逻！",
  "hover-kernelhub":
    "离开工位心慌？挂上神枢通道，去星巴克也能在手机上批准那行致命的 rm -rf！[调阅神枢 ->]",
  "hover-vault":
    "不带盘缠怎么当赛博骑士？多链资产、美元账户与全球 Visa 刷卡已就绪！[检视行囊 ->]",
  "poke-spam":
    "喂！编译还在跑，代码还没写完，一直戳我是不会凭空变出零 Bug 架构的！",
  idle: "呼……宿主掉线了？终端连接已保持保活状态，放心走开吧。",
} as const;

export type FirstLineMood = keyof typeof BUDDY_FIRST_LINE_TEXTS;

/**
 * iCLOSER 身份的首次原文替换（自我介绍与口吻切换）。
 * 键=FirstLineMood；CTA 方括号段逐字保留（BuddyBubble 按 [label] 链接化）。
 */
export const ICLOSER_FIRST_LINES: Record<
  FirstLineMood,
  { text: string; action: string }
> = {
  onload: {
    text: "滴。iCloser 灵机上线——你的资产哨兵。钥匙圈、钱柜与全球 Visa 已挂载，随时出征。",
    action: "降落入场",
  },
  "hover-kernelhub": {
    text: "神枢通道挂上，审批卡同步进 iCloser 钥匙圈。授权了什么一目了然，rm -rf 也得过核销。[调阅神枢 ->]",
    action: "掏出像素对讲机",
  },
  "hover-vault": {
    text: "钱柜盘缠我背：多链资产、美元账户与全球 Visa 已就绪，骑士只管出征。[检视行囊 ->]",
    action: "高举黄金宝箱",
  },
  "poke-spam": {
    text: "喂！手续费按戳计算。再戳我就把你的结算费率调到 T+1 溢价！",
    action: "叉腰怒目 >_<",
  },
  idle: {
    text: "呼……宿主掉线？钱柜保活巡检已挂起，金库有我盯着，放心走开。",
    action: "打瞌睡 Zzz",
  },
};

/**
 * 气泡呈现层身份映射：iCLOSER 身份把工单 5 首次原文换成 iCloser 自我介绍/口吻。
 * 轮换台词已由 pickLineByTrigger 按身份出词，此处按原文逐字命中才替换（不误伤轮换句）。
 * kernelhub 原样返回（信 XIN 外观/口吻保持）。
 */
export function resolveIdentityLine<
  T extends { text: string; action: string },
>(line: T, identity: BuddyIdentity): T {
  if (identity !== "icloser") return line;
  for (const mood of Object.keys(BUDDY_FIRST_LINE_TEXTS) as FirstLineMood[]) {
    if (BUDDY_FIRST_LINE_TEXTS[mood] === line.text) {
      return { ...line, ...ICLOSER_FIRST_LINES[mood] } as T;
    }
  }
  return line;
}

/** 出场亮相自我介绍（START 时经 xin:bubble 通道播报；按当前身份取口吻） */
export function identityGreeting(identity: BuddyIdentity): string {
  return identity === "icloser"
    ? ICLOSER_FIRST_LINES.onload.text
    : BUDDY_FIRST_LINE_TEXTS.onload;
}

/**
 * 从指定池抽一条（可排除刚说过的一条；rand 注入便于测试确定性）。
 * 池为空时抛错——测试保证三池非空。
 */
export function pickVoiceLine(
  persona: VoicePersona,
  opts: { excludeId?: string; rand?: () => number } = {},
): VoiceLine {
  const pool = VOICE_POOLS[persona];
  const rand = opts.rand ?? Math.random;
  const candidates = opts.excludeId
    ? pool.filter((l) => l.id !== opts.excludeId)
    : pool;
  const pickFrom = candidates.length > 0 ? candidates : pool;
  const idx = Math.floor(rand() * pickFrom.length) % pickFrom.length;
  return pickFrom[idx];
}

/**
 * 按触发场景抽台词（跨人格面：先看触发专属池，再退化到通用池）。
 * 出场角色身份生效：icloser → ICLOSER_LINES 池；kernelhub → 原三池（行为不变）。
 * opts.identity 可显式指定（测试/预览用），缺省取 getBuddyIdentity()。
 */
export function pickLineByTrigger(
  trigger: VoiceTrigger,
  opts: {
    excludeId?: string;
    rand?: () => number;
    identity?: BuddyIdentity;
  } = {},
): VoiceLine {
  const identity = opts.identity ?? getBuddyIdentity();
  const pool = identity === "icloser" ? ICLOSER_LINES : ALL_VOICE_LINES;
  const fallback = identity === "icloser" ? ICLOSER_LINES : GENERAL_LINES;
  const matches = pool.filter((l) => l.trigger === trigger);
  const rand = opts.rand ?? Math.random;
  const candidates = opts.excludeId
    ? matches.filter((l) => l.id !== opts.excludeId)
    : matches;
  const pickFrom = candidates.length > 0 ? candidates : fallback;
  const idx = Math.floor(rand() * pickFrom.length) % pickFrom.length;
  return pickFrom[idx];
}
